import { useState, useEffect, useRef } from "react";
import { useLanguageContext } from "../../Context/LanguageContext";
import EquipmentGroupActions from "../../Actions/EquipmentGroupActions";
import equipmentGroupStore from "../../Stores/EquipmentGroupStore";
import EquipmentGroupContants from "../../Constants/EquipmentGroupConstants";
import { useGlobalSpinnerActionsContext } from "../../Context/GlobalSpinnerContext";
import InspectionReportAttributeSelector from "./InspectionReportAttributeSelector";
import { PageHeader } from "../MuiOverRideComponent/PageHeader";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import InspectionReportPreview from "./InspectionReportPreview";

export default function InspectionReports() {
  const { localization } = useLanguageContext();
  const setGlobalSpinner = useGlobalSpinnerActionsContext();
  const [egList, setEgList] = useState([]);
  const [egId, setEgId] = useState("");
  const [standardAttributes, setStandardAttributes] = useState([]);
  const [selectedReportAttributes, setSelectedReportAttributes] = useState([]);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [reportConfig, setReportConfig] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const selectedEquipmentGroupId = useRef("");

  const handleAttributeSelectionChange = (selectedAttributes) => {
    setSelectedReportAttributes(selectedAttributes);
  };

  const getEquipmentGroupByOrganisations = () => {
    setGlobalSpinner(true);
    EquipmentGroupActions.getOrganisationsEquipmentGroup();
  };

  const getStandardAttribute = (eqGroupId) => {
    setGlobalSpinner(true);
    EquipmentGroupActions.getAnlagegruppeStandardAttributes(eqGroupId);
  };

  const getInspectionReportConfiguration = () => {
    setGlobalSpinner(true);
    EquipmentGroupActions.getInspectionReportConfiguration(
      selectedEquipmentGroupId.current,
    );
  };

  const saveInspectionReportConfiguration = () => {
    setGlobalSpinner(true);
    const data = {
      EquipmentGroupId: egId,
      //iterate on selectedReportAttributes and return  object with StandardAttributeId and Position
      InspectionReportSettings: selectedReportAttributes.map((attr, index) => ({
        StandardAttributeId: attr.attributeID,
        Position: index + 1,
      })),
    };
    EquipmentGroupActions.saveInspectionReportConfiguration(data);
  };

  useEffect(() => {
    const selectedIds = new Set(
      selectedReportAttributes.map((attr) => attr.id),
    );
    setAvailableAttributes(
      standardAttributes.filter((attr) => !selectedIds.has(attr.id)),
    );
  }, [selectedReportAttributes, standardAttributes]);

  useEffect(() => {
    selectedEquipmentGroupId.current = egId;
    if (egId != "") {
      getStandardAttribute(egId);
    } else {
      setStandardAttributes([]);
    }
    setSelectedReportAttributes([]); // Reset selected attributes when equipment group changes
    setReportConfig(null); // Reset report config
    return () => {};
  }, [egId]);

  useEffect(() => {
    getEquipmentGroupByOrganisations();
    function getEquipmentGroupSuccess() {
      const res = equipmentGroupStore.getOrgEquipmentGroupResult();

      if (res.length > 0) {
        let sortList = res.sort((a, b) =>
          a.werk > b.werk ? 1 : a.werk < b.werk ? -1 : 0,
        );
        setEgList(sortList);
        setEgId(sortList[0].id);
        selectedEquipmentGroupId.current = sortList[0].id;
      } else {
        setEgList([]);
      }
    }
    function getEquipmentGroupFailure() {
      const res = equipmentGroupStore.getOrgEquipmentGroupResult();

      setGlobalSpinner(false);
    }
    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.GET_ORG_EQUIPMENT_SUCCESS,
      getEquipmentGroupSuccess,
    );
    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.GET_ORG_EQUIPMENT_FAILURE,
      getEquipmentGroupFailure,
    );
    return () => {
      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.GET_ORG_EQUIPMENT_SUCCESS,
        getEquipmentGroupSuccess,
      );
      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.GET_ORG_EQUIPMENT_FAILURE,
        getEquipmentGroupFailure,
      );
    };
  }, []);

  useEffect(() => {
    function getStandardAttributeSuccess() {
      const res = equipmentGroupStore.getStandardAttributes();

      if (res.anlagegruppeStandardAttributeSaveUpdateModels.length > 0) {
        setStandardAttributes(
          res.anlagegruppeStandardAttributeSaveUpdateModels,
        );
      } else {
        setStandardAttributes([]);
      }
      getInspectionReportConfiguration();
      //setGlobalSpinner(false);
    }

    function getStandardAttributeFailure() {
      const res = equipmentGroupStore.getStandardAttributes();
      setGlobalSpinner(false);
    }

    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.GET_EQU_GUP_STANDARD_ATTRIBUTES_SUCCESS,
      getStandardAttributeSuccess,
    );

    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.GET_EQU_GUP_STANDARD_ATTRIBUTES_FAILURE,
      getStandardAttributeFailure,
    );
    return () => {
      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.GET_EQU_GUP_STANDARD_ATTRIBUTES_SUCCESS,
        getStandardAttributeSuccess,
      );

      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.GET_EQU_GUP_STANDARD_ATTRIBUTES_FAILURE,
        getStandardAttributeFailure,
      );
    };
  }, []);

  useEffect(() => {
    function saveInspectionReportAttrSuccess() {
      const res = equipmentGroupStore.getSaveInspectionReportAttrRes();
      if (res) {
        toast.success(localization.inspectionReportConfigSaveSuccess);
      }
      setGlobalSpinner(false);
    }
    function saveInspectionReportAttrFailure() {
      const res = equipmentGroupStore.getSaveInspectionReportAttrRes();

      setGlobalSpinner(false);
    }
    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.SAVE_INSPECTION_REPORT_ATTR_SUCCESS,
      saveInspectionReportAttrSuccess,
    );
    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.SAVE_INSPECTION_REPORT_ATTR_FAILURE,
      saveInspectionReportAttrFailure,
    );
    return () => {
      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.SAVE_INSPECTION_REPORT_ATTR_SUCCESS,
        saveInspectionReportAttrSuccess,
      );
      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.SAVE_INSPECTION_REPORT_ATTR_FAILURE,
        saveInspectionReportAttrFailure,
      );
    };
  }, []);

  const processReportConfiguration = (config, allAttributes) => {
    if (config && config.length > 0 && allAttributes.length > 0) {
      const selectedConfig = config
        .map((configItem) => {
          const attribute = allAttributes.find(
            (attr) => attr.attributeID === configItem.standardAttributeId,
          );
          return attribute
            ? { ...attribute, position: configItem.position }
            : null;
        })
        .filter(Boolean) // Remove nulls if any attribute not found
        .sort((a, b) => a.position - b.position); // Sort by position

      setSelectedReportAttributes(selectedConfig);
    } else {
      setSelectedReportAttributes([]);
    }
  };

  useEffect(() => {
    if (reportConfig && standardAttributes.length > 0) {
      processReportConfiguration(reportConfig, standardAttributes);
    }
  }, [reportConfig, standardAttributes]);

  useEffect(() => {
    function getInspectionReportConfSuccess() {
      const res = equipmentGroupStore.getInspectionReportAttrRes();
      setReportConfig(res);
      setGlobalSpinner(false);
    }

    function getInspectionReportConfFailure() {
      const res = equipmentGroupStore.getInspectionReportAttrRes();
      setGlobalSpinner(false);
    }

    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.GET_INSPECTION_REPORT_CONF_SUCCESS,
      getInspectionReportConfSuccess,
    );

    equipmentGroupStore.addChangeListener(
      EquipmentGroupContants.GET_INSPECTION_REPORT_CONF_FAILURE,
      getInspectionReportConfFailure,
    );
    return () => {
      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.GET_INSPECTION_REPORT_CONF_SUCCESS,
        getInspectionReportConfSuccess,
      );

      equipmentGroupStore.removeChangeListener(
        EquipmentGroupContants.GET_INSPECTION_REPORT_CONF_FAILURE,
        getInspectionReportConfFailure,
      );
    };
  }, []);

  return (
    <>
      <div id="content">
        <div>
          <PageHeader Title={localization.inspectionReportPageTitile} />
        </div>
        <div className="col-4 form-group d-flex  align-items-center pl-0">
          <label htmlFor="equipmentGroupList" className="mr-3">
            {localization.equipmentGroup}
          </label>
          <select
            className="form-control equipment-group-select"
            id="equipmentGroupList"
            name="equipmentGroupList"
            required
            value={egId}
            onChange={(e) => {
              setEgId(e.target.value);
            }}
          >
            <option value="0" disabled>
              {localization.selectEquipmentGroup}
            </option>
            {egList.map((row, index) => {
              return (
                <option key={index} value={row.id}>
                  {row.bezeichnungsDisplay}
                </option>
              );
            })}
          </select>
        </div>
        <div className="attribute-selector-wrapper">
          <InspectionReportAttributeSelector
            availableAttributes={availableAttributes}
            selectedAttributes={selectedReportAttributes}
            onSelectionChange={handleAttributeSelectionChange}
          />
        </div>
        <div className="row justify-content-center mt-3 mb-3">
          <div className="col-auto">
            <Button
              variant="contained"
              className="btn-download-preview"
              disabled={selectedReportAttributes.length == 0}
              onClick={() => {
                setOpenPreview(true);
              }}
            >
              {localization.preview}
            </Button>
            <Button
              variant="contained"
              className="btn-save"
              disabled={selectedReportAttributes.length == 0}
              onClick={saveInspectionReportConfiguration}
            >
              {localization.save}
            </Button>
          </div>
        </div>
      </div>
      {openPreview && (
        <InspectionReportPreview
          open={openPreview}
          onClose={() => {
            setOpenPreview(false);
          }}
          selectedAttributes={selectedReportAttributes}
        />
      )}
    </>
  );
}
